/**
 * The eight ideology packages (docs/design-ideology.json `roster`). An ideology is a doctrine package:
 * government rules, factions, strengths, weaknesses, a unique ability, objectives and a climate lever.
 * Starting stocks and flows here are ideology modifiers only; slot modifiers live in slots.ts.
 *
 * `inspiredBy` is TEACHER-ONLY. It must never appear in a student or projector view before the debrief.
 * Student text uses only in-world names and the neutral student-facing wording.
 */
import type {
  AxisVector,
  CountryId,
  Forces,
  IndexValues,
  OrderKind,
  PayoutFormula,
  PledgeRoute,
  ProgramId,
} from '../../shared/types.ts';
import { ROYAL_GRANTS } from './rules.ts';

/** Who must sign or ratify. Counts only members present that day (attendance toggle). */
export type Approval = 'head' | 'head_plus_one' | 'head_plus_foreign' | 'unanimous' | 'any_minister';

export interface GovernmentRules {
  /** Rule for signing an ordinary set of turn orders. */
  signOff: Approval;
  /** Rule when the set contains a "major" order. */
  majorSignOff: Approval;
  majorOrders: OrderKind[];
  /** Expand Fossil counts as major (Alvera's coalition rule). */
  fossilIsMajor: boolean;
  /** Rule for ratifying a treaty. */
  treatyRatify: Approval;
  /** Orders the Head of State may approve alone per turn (decree power); the set must be this small. */
  decree: number;
  supportVisibility: 'full' | 'band';
  headRotates: boolean;
  headTitle: string;
  text: string;
}

export interface FactionData {
  id: string;
  name: string;
  wants: string;
}

export interface AbilityData {
  id: string;
  name: string;
  text: string;
  /** Kestran's Five-Year Plan is used on fixed turns instead of once per game. */
  repeatTurns: readonly number[] | null;
}

export interface AiDoctrine {
  securityTarget: number;
  /** 0 = rarely green, 2 = green whenever it can. */
  greenBias: number;
  aidBias: number;
  treatyBias: number;
  allianceOk: boolean;
  usesTariffs: boolean;
  pledgeRoute: PledgeRoute;
  formula: PayoutFormula;
  /** Votes on binding Assembly formulas. */
  bindingVotes: 'yes' | 'no';
  programs: ProgramId[];
  refugeesAccept: number;
}

export interface IdeologyData {
  id: CountryId;
  name: string;
  shortName: string;
  /** TEACHER-ONLY real-world label. Revealed at the debrief. */
  inspiredBy: string;
  /** The only description students see before the debrief. */
  oneLiner: string;
  coreValues: string;
  government: string;
  rules: GovernmentRules;
  start: {
    treasury: number;
    energy: number;
    food: number;
    support: number;
    forces: Partial<Forces>;
    index: IndexValues;
    lockedFund: number;
  };
  flows: {
    /** Treasury income modifier per turn. */
    treasury: number;
    /** Treasury upkeep modifier per turn (positive = more upkeep). */
    upkeep: number;
    energy: number;
    energyUpkeep: number;
    food: number;
  };
  strengths: string[];
  weaknesses: string[];
  ability: AbilityData;
  climateLever: string;
  factions: FactionData[];
  objectives: [string, string];
  vector: AxisVector;
  accent: string;
  pattern: string;
  roleWords: string;
  ai: AiDoctrine;
}

export const IDEOLOGIES: Record<CountryId, IdeologyData> = {
  ironvale: {
    id: 'ironvale',
    name: 'The Republic of Ironvale',
    shortName: 'Ironvale',
    inspiredBy:
      'The US Republican Party as written in its 2024 platform (America First: growth through tax cuts, energy dominance, tariffs as a bargaining tool, peace through strength with allies who share the burden).',
    oneLiner:
      'A republic that puts its own people first. It trusts free business and plenty of energy. It keeps the peace by being strong.',
    coreValues:
      'Freedom under a written plan of government. Free business and low taxes. Plenty of energy. We make our own choices as a nation. Fair trade. Strong families and towns. Peace through strength, with allies who do their part.',
    government:
      'A constitutional federal republic. The elected President (Head of State) can veto treaties and budgets. State Governors keep local programs local, so local programs cost 1 less. Public Support is fully visible.',
    rules: {
      signOff: 'head',
      majorSignOff: 'head_plus_one',
      majorOrders: ['declare_war', 'attack', 'ability', 'leave_treaty'],
      fossilIsMajor: false,
      treatyRatify: 'head_plus_one',
      decree: 0,
      supportVisibility: 'full',
      headRotates: false,
      headTitle: 'President',
      text: 'Big moves need the President and one other minister who is present that day. Big moves are war, attacks, the unique ability, treaties and leaving a treaty.',
    },
    start: {
      treasury: 0, energy: 4, food: 0, support: 62,
      forces: { army: 1, air: 1 },
      index: { prosperity: 6, security: 6, wellbeing: -6, environment: -6 },
      lockedFund: 0,
    },
    flows: { treasury: -1, upkeep: 0, energy: 0, energyUpkeep: 0, food: 0 },
    strengths: [
      'Growth Dividend: +1 Treasury per turn for every full 10 Prosperity above 50 (up to +3).',
      'Energy Dominance: you start with 4 more Energy. Each time you sell Energy to a treaty partner, you earn 1 more Treasury.',
      'Leverage: if a nation you have tariffed signs a trade deal with you, the tariff ends at once with no penalty.',
      'Constitutional Stability: no single event can cut Public Support by more than 8. Army and Air Wing +1 each.',
    ],
    weaknesses: [
      'Tax-Cut Bet: at each plan check (Turns 4, 8, 12, 16, 20), if Prosperity has not risen since the last check, the deficit costs 2 Treasury and the Growth Dividend pays nothing next turn.',
      'Alliance Strain: tariffing a treaty partner makes its defense promises to you lapse for 3 turns.',
      'Thinner safety net: crisis hits to Well-being are 1 larger until you build the Community and Charity Network (2 Treasury).',
    ],
    ability: {
      id: 'peace_through_strength',
      name: 'Peace Through Strength',
      text: 'Once per game. For 3 turns, Ironvale and up to 2 treaty allies get +30% defense and +3 Security, and any nation that attacks them loses 8 Public Support. Ironvale’s military upkeep drops by 1 during the umbrella.',
      repeatTurns: null,
    },
    climateLever:
      'Energy Innovation Act (reusable): pay 4 Treasury for Carbon -1 with no Energy loss. At Carbon 0 it gives +1 clean Energy per turn instead. It can be given to another nation as a Clean Tech Transfer.',
    factions: [
      { id: 'iv_families', name: 'Families and Neighborhoods', wants: 'Well-being of 50 or more.' },
      { id: 'iv_producers', name: 'Workers and Producers', wants: 'Rising Prosperity and fair trade deals.' },
      { id: 'iv_hawks', name: 'Security Hawks', wants: 'A military at least as big as at the start.' },
      { id: 'iv_young', name: 'Young Conservatives', wants: 'Energy innovation and a Treasury of 8 or more.' },
    ],
    objectives: ['ironvale_solvent', 'ironvale_energy'],
    vector: { economy: 0.65, liberty_order: 0, tradition_progress: -0.65, sovereignty_global: -0.54, growth_green: -0.65 },
    accent: '#E69F00',
    pattern: 'stripes',
    roleWords: 'a republic that builds strength through free enterprise and energy',
    ai: {
      securityTarget: 60, greenBias: 1, aidBias: 0, treatyBias: 1, allianceOk: true, usesTariffs: false,
      pledgeRoute: 'clean_tech_transfer', formula: 'damage', bindingVotes: 'no',
      programs: ['community_charity', 'infrastructure', 'schools', 'health_service', 'public_safety', 'farm_support', 'civic_inclusion', 'energy_grid'],
      refugeesAccept: 1,
    },
  },

  brightwater: {
    id: 'brightwater',
    name: 'The Commonwealth of Brightwater',
    shortName: 'Brightwater',
    inspiredBy:
      'The US Democratic Party as written in its 2024 platform, limited to the game’s five policy axes (middle-out growth, unions, safety net, alliances, clean energy).',
    oneLiner: 'A democracy that protects rights. Its market has fair rules and a strong safety net. It works with allies and builds clean energy.',
    coreValues:
      'Equal rights and a fair chance for all. Growth that starts with working families. Fair pay and strong unions. A safety net for hard times. Friends and world groups we work with. A move to clean energy.',
    government:
      'A constitutional democracy with independent courts and a professional civil service. Treaties and war need both the Head of State and the Foreign Minister, like a Senate vote. Public Support is fully visible.',
    rules: {
      signOff: 'head',
      majorSignOff: 'head_plus_foreign',
      majorOrders: ['declare_war'],
      fossilIsMajor: false,
      treatyRatify: 'head_plus_foreign',
      decree: 0,
      supportVisibility: 'full',
      headRotates: false,
      headTitle: 'Head of State',
      text: 'Treaties and war declarations need the Head of State and the Foreign Minister (counting members present that day).',
    },
    start: {
      treasury: 0, energy: 0, food: 0, support: 60,
      forces: { army: -1 },
      index: { prosperity: -4, security: -8, wellbeing: 8, environment: 4 },
      lockedFund: 0,
    },
    flows: { treasury: 1, upkeep: 1, energy: 0, energyUpkeep: 0, food: 0 },
    strengths: [
      'Alliance Builder: embassies and treaties cost 1 less Treasury.',
      'Safety net: food shortfalls cost 1 less Well-being, and Well-being starts 8 higher.',
      'Clean Energy Investment: Green Transition costs 2 and adds +1 Prosperity (green jobs). You may pay for one in another nation as a Clean Tech Transfer.',
      'Independent Institutions: enemy spy missions are 10 points less likely to work, and your human-rights treaties give both signers +2 Well-being.',
    ],
    weaknesses: [
      'High spending: +1 Treasury upkeep, so you reach the credit-downgrade line (Treasury below 3: -4 Prosperity) sooner than most.',
      'Slow consensus: two ministers confirm major actions, and if Labor and the Climate and Youth Movement are both unhappy in one turn, Public Support -6.',
      'Lower starting Security and a smaller Army (-1).',
    ],
    ability: {
      id: 'grand_coalition',
      name: 'Grand Coalition Summit',
      text: 'Once per game. Call a world summit. Every nation that signs a treaty this turn gets +3 Well-being, Brightwater gets +5 Security, and Brightwater adds one item to the next World Assembly.',
      repeatTurns: null,
    },
    climateLever: 'Clean Energy Investment: Green Transition costs 2 and adds +1 Prosperity; it can be paid for in another nation.',
    factions: [
      { id: 'bw_labor', name: 'Labor and Unions', wants: 'Food security and good jobs (Well-being of 50 or more, no food shortfall).' },
      { id: 'bw_climate', name: 'Climate and Youth Movement', wants: 'Carbon cuts.' },
      { id: 'bw_moderates', name: 'Moderates and Suburban Voters', wants: 'Prosperity of 50 or more and Treasury above 5.' },
      { id: 'bw_justice', name: 'Justice and Charity Networks', wants: 'Food aid given, a welcome for refugees, and rights treaties.' },
    ],
    objectives: ['brightwater_order', 'brightwater_green'],
    vector: { economy: -0.23, liberty_order: -0.58, tradition_progress: 0.92, sovereignty_global: 0.35, growth_green: 0.46 },
    accent: '#56B4E9',
    pattern: 'dots',
    roleWords: 'a democracy that builds alliances, a safety net and clean energy',
    ai: {
      securityTarget: 50, greenBias: 2, aidBias: 2, treatyBias: 2, allianceOk: true, usesTariffs: false,
      pledgeRoute: 'clean_tech_transfer', formula: 'vulnerability', bindingVotes: 'yes',
      programs: ['health_service', 'schools', 'civic_inclusion', 'infrastructure', 'energy_grid', 'farm_support', 'public_safety'],
      refugeesAccept: 2,
    },
  },

  meridia: {
    id: 'meridia',
    name: 'The Free State of Meridia',
    shortName: 'Meridia',
    inspiredBy: 'The Libertarian Party platform and classical-liberal thought. No live national example is used.',
    oneLiner: 'A free society. It is built on personal freedom, the right to own things, free trade and peace with every nation.',
    coreValues:
      'Each person is free. People may own what they earn. Trade is free and money keeps its value. People help each other by choice, not by force. We want peace and trade with all nations, and binding alliances with none.',
    government:
      'A minimal constitutional republic with a strong bill of rights, a balanced-budget rule and no draft. Any minister may act alone within their own desk. Public Support is fully visible.',
    rules: {
      signOff: 'any_minister',
      majorSignOff: 'any_minister',
      majorOrders: [],
      fossilIsMajor: false,
      treatyRatify: 'any_minister',
      decree: 0,
      supportVisibility: 'full',
      headRotates: false,
      headTitle: 'First Citizen',
      text: 'Any minister may sign and act alone within their own desk. Every desk should still file at least 1 action per turn.',
    },
    start: {
      treasury: 6, energy: 0, food: 0, support: 60,
      forces: { navy: -1 },
      index: { prosperity: 10, security: -2, wellbeing: -4, environment: -4 },
      lockedFund: 0,
    },
    flows: { treasury: 0, upkeep: -2, energy: 0, energyUpkeep: 0, food: 0 },
    strengths: [
      'Strong Treasury: +6 Treasury at the start and upkeep 2 lower every turn.',
      'Best trade terms: +1 unit on every trade deal of 4 or more units.',
      'Citizen Militia: +20% defense at home.',
      'Innovation economy: Prosperity starts 10 higher. The Innovation Prize makes the next Green Transition by any nation cheaper.',
    ],
    weaknesses: [
      'No central stockpiles: you cannot hold more than 8 Energy or 8 Food at the end of a turn. Extra is sold at 2 units for 1 Treasury.',
      'Public-goods gap: the drought and the pandemic cost double Well-being unless you have a Mutual Aid Compact or the Civic Association Network (3 Treasury).',
      'Joining a military alliance costs 6 Public Support, and the Navy is 1 smaller.',
    ],
    ability: {
      id: 'free_port',
      name: 'Free Port Charter',
      text: 'Once per game. Declare a Free Trade Zone with one partner for 4 turns. Both gain +1 Treasury per turn, and trades between you get +1 unit in both directions.',
      repeatTurns: null,
    },
    climateLever:
      'Innovation Prize (reusable): pay 2 Treasury. The next Green Transition by any nation costs 1 less, and Meridia gains +3 Prosperity when it is claimed. Each prize counts 2 toward Meridia’s fair share.',
    factions: [
      { id: 'md_enterprise', name: 'Free Enterprise League', wants: 'Rising Prosperity.' },
      { id: 'md_liberties', name: 'Civil Liberties Union', wants: 'No domestic surveillance (counterintelligence costs 3 Public Support).' },
      { id: 'md_peace', name: 'Non-Interventionists', wants: 'No military alliances and no foreign wars.' },
      { id: 'md_mutual', name: 'Mutual Aid Societies', wants: 'Crises handled by civil society (charities, clubs and lodges).' },
    ],
    objectives: ['meridia_markets', 'meridia_peace'],
    vector: { economy: 0.85, liberty_order: -0.85, tradition_progress: 0.28, sovereignty_global: 0.09, growth_green: -0.19 },
    accent: '#F0E442',
    pattern: 'grid',
    roleWords: 'a free society that trusts markets, trade and voluntary cooperation',
    ai: {
      securityTarget: 45, greenBias: 1, aidBias: 0, treatyBias: 1, allianceOk: false, usesTariffs: false,
      pledgeRoute: 'innovation_prize', formula: 'equal', bindingVotes: 'no',
      programs: ['civic_association', 'infrastructure', 'schools', 'health_service', 'energy_grid', 'farm_support', 'public_safety'],
      refugeesAccept: 1,
    },
  },

  kestran: {
    id: 'kestran',
    name: 'The People’s Development Republic of Kestran',
    shortName: 'Kestran',
    inspiredBy:
      '"Socialism with Chinese characteristics", the official ideology of the People’s Republic of China. In the game it is culturally and ethnically unmarked.',
    oneLiner: 'One party leads the country. It makes long plans and uses a market economy. Its goals are shared wealth and a calm, stable society.',
    coreValues:
      'One party leads the country forward. We plan many years ahead. We want shared wealth and an end to poverty. We keep society calm and stable. Nations should not meddle in each other’s affairs.',
    government:
      'A one-party state. The Development Party leads a State Council. The Chair may approve one small set of orders alone; everything else needs a second minister. The Chair role rotates every 5 turns. Your team sees Public Support only as a 10-point band.',
    rules: {
      signOff: 'head_plus_one',
      majorSignOff: 'head_plus_one',
      majorOrders: [],
      fossilIsMajor: false,
      treatyRatify: 'head_plus_one',
      decree: 1,
      supportVisibility: 'band',
      headRotates: true,
      headTitle: 'Chair of the State Council',
      text: 'The Chair may approve a set of 1 order alone. Anything more, and every treaty, needs a second minister. The Chair rotates every 5 turns.',
    },
    start: {
      treasury: 0, energy: 0, food: 2, support: 58,
      forces: {},
      index: { prosperity: 6, security: 6, wellbeing: -4, environment: -8 },
      lockedFund: 0,
    },
    flows: { treasury: 2, upkeep: 0, energy: 0, energyUpkeep: 1, food: 0 },
    strengths: [
      'Development engine: every program that raises Well-being gives +2 extra Well-being.',
      'Large industrial base: +2 Treasury per turn.',
      'Green Industrial Drive: the only nation that may take two Green Transitions in one turn (5 Treasury, Carbon -2), and it may fund Green Development Loans in other nations.',
      'Strong counterintelligence: enemy spy missions are 20 points less likely to work.',
    ],
    weaknesses: [
      'Fragile, partly hidden Public Support: when true Public Support is below 40, a d10 roll each turn of 1-3 means unrest (-5 Prosperity, -5 Public Support).',
      'Secrecy: your first treaty with each nation costs 1 more Treasury. You can add an Openness Clause instead, but your security services dislike it (-2 Public Support).',
      'Hungry factories and limited freedoms: your Energy upkeep is 1 higher, and Well-being cannot go above 85.',
    ],
    ability: {
      id: 'five_year_plan',
      name: 'Five-Year Plan',
      text: 'On Turns 1, 5, 9, 13 and 17. Move up to 6 units among Treasury, Energy and Food (1 for 1), and fast-track one program so it finishes this turn.',
      repeatTurns: [1, 5, 9, 13, 17],
    },
    climateLever: 'Green Industrial Drive: two Green Transitions in one turn for 5 Treasury, or a Green Development Loan (a Green Transition in another nation for 3 Treasury).',
    factions: [
      { id: 'ks_planners', name: 'Party Planners', wants: 'Prosperity higher than 4 turns ago.' },
      { id: 'ks_coastal', name: 'Coastal Entrepreneurs', wants: 'Trade deals and openness.' },
      { id: 'ks_rural', name: 'Rural Provinces', wants: 'No food shortfall and rising Well-being.' },
      { id: 'ks_security', name: 'Security Apparatus', wants: 'Security of 60 or more and few Transparency Clauses.' },
    ],
    objectives: ['kestran_prosperity', 'kestran_rise'],
    vector: { economy: -0.78, liberty_order: 0.89, tradition_progress: 0.11, sovereignty_global: -0.33, growth_green: 0.22 },
    accent: '#009E73',
    pattern: 'chevron',
    roleWords: 'a planned development state that aims for common prosperity and stability',
    ai: {
      securityTarget: 60, greenBias: 1, aidBias: 1, treatyBias: 2, allianceOk: true, usesTariffs: false,
      pledgeRoute: 'green_development_loan', formula: 'equal', bindingVotes: 'yes',
      programs: ['health_service', 'schools', 'infrastructure', 'farm_support', 'civic_inclusion', 'public_safety', 'energy_grid'],
      refugeesAccept: 0,
    },
  },

  vellmark: {
    id: 'vellmark',
    name: 'The Social Commonwealth of Vellmark',
    shortName: 'Vellmark',
    inspiredBy: 'Nordic-model social democracy and European social-democratic parties (the S&D group in the European Parliament).',
    oneLiner: 'A democracy where people trust each other. Its open market pays for services for everyone. Unions and bosses bargain together.',
    coreValues:
      'Schools, health care and other services for everyone. High taxes pay for them. Most businesses are private and the market is open. People should be equal. Unions and bosses make deals together. People trust each other. We work with other nations and care for nature.',
    government:
      'A democracy run by a parliament. Several parties share the cabinet, and town councils are strong. Public Support is visible and starts high (66).',
    rules: {
      signOff: 'head',
      majorSignOff: 'head_plus_one',
      majorOrders: ['declare_war'],
      fossilIsMajor: false,
      treatyRatify: 'head',
      decree: 0,
      supportVisibility: 'full',
      headRotates: false,
      headTitle: 'Prime Minister',
      text: 'The Prime Minister signs orders. A war declaration needs the Prime Minister plus one other minister present that day.',
    },
    start: {
      treasury: 0, energy: 0, food: 0, support: 66,
      forces: { army: -1, air: -1 },
      index: { prosperity: -6, security: -10, wellbeing: 10, environment: 6 },
      lockedFund: 0,
    },
    flows: { treasury: -1, upkeep: 0, energy: 0, energyUpkeep: 0, food: 0 },
    strengths: [
      'Top Well-being start (+10).',
      'Resilient society: crisis hits to Well-being and Public Support are cut in half (rounded down).',
      'High trust: Public Support starts at 66.',
      'Green Public Investment: one Green Transition per game is free, and Environment starts 6 higher.',
    ],
    weaknesses: [
      'Tax burden: 1 less Treasury income every turn.',
      'Aging population: upkeep rises by 1 at Turn 8 and again at Turn 14, unless you build Workforce Renewal (3 Treasury) or accept 2 or more refugee groups at Turn 14.',
      'Small military (Army -1, Air Wing -1) and the lowest starting Security.',
    ],
    ability: {
      id: 'tripartite_accord',
      name: 'Tripartite Accord',
      text: 'Once per game. Unions, employers and government sign a pact: +4 Treasury now, +5 Prosperity, and faction moods are frozen for 3 turns.',
      repeatTurns: null,
    },
    climateLever: 'Green Public Investment: one free Green Transition per game.',
    factions: [
      { id: 'vm_unions', name: 'Trade Union Confederation', wants: 'Well-being of 60 or more.' },
      { id: 'vm_business', name: 'Business Federation', wants: 'Prosperity of 45 or more.' },
      { id: 'vm_green', name: 'Green Youth', wants: 'Carbon cuts and a fair share of climate finance.' },
      { id: 'vm_civic', name: 'Voluntary Sector and Town Councils', wants: 'Aid given and Resilience investment.' },
    ],
    objectives: ['vellmark_nobody', 'vellmark_pathfinder'],
    vector: { economy: -0.49, liberty_order: 0.29, tradition_progress: 0.1, sovereignty_global: 0.78, growth_green: 0.78 },
    accent: '#CC79A7',
    pattern: 'waves',
    roleWords: 'a high-trust welfare democracy that shares with other nations',
    ai: {
      securityTarget: 45, greenBias: 2, aidBias: 2, treatyBias: 2, allianceOk: true, usesTariffs: false,
      pledgeRoute: 'fund_pledge', formula: 'vulnerability', bindingVotes: 'yes',
      programs: ['workforce_renewal', 'health_service', 'schools', 'civic_inclusion', 'infrastructure', 'energy_grid', 'public_safety'],
      refugeesAccept: 2,
    },
  },

  alvera: {
    id: 'alvera',
    name: 'The Social Market Federation of Alvera',
    shortName: 'Alvera',
    inspiredBy:
      'European Christian democracy (the EPP, the largest group in the European Parliament). Its link to Catholic social teaching appears only in the teacher appendix.',
    oneLiner:
      'A democracy of many regions. It respects every person. It makes choices close to home, backs strong families and a fair market, and cares for nature.',
    coreValues:
      'Every person has worth. Choices are made as close to home as they can be. Families come first. The market is free but fair, and workers are protected. We stand by one another. We care for the natural world.',
    government:
      'A federal parliamentary democracy with strong regions and a coalition cabinet. A Council of Regions is consulted on programs. Public Support is visible and starts at 62.',
    rules: {
      signOff: 'head',
      majorSignOff: 'unanimous',
      majorOrders: ['declare_war', 'leave_treaty'],
      fossilIsMajor: true,
      treatyRatify: 'head',
      decree: 0,
      supportVisibility: 'full',
      headRotates: false,
      headTitle: 'Chancellor',
      text: 'Everyone on the team who is present must sign to declare war, leave a treaty or Expand Fossil.',
    },
    start: {
      treasury: 0, energy: 0, food: 0, support: 62,
      forces: {},
      index: { prosperity: -2, security: -4, wellbeing: 4, environment: 2 },
      lockedFund: 0,
    },
    flows: { treasury: 0, upkeep: 0, energy: 0, energyUpkeep: 0, food: 0 },
    strengths: [
      'Subsidiarity: Resilience levels and local programs cost 1 less Treasury.',
      'Solidarity aid: any aid you give delivers 1 extra unit (give 3 Food, the partner receives 4).',
      'Family Firms: +1 Food and +1 Treasury per turn while Well-being is 55 or more.',
      'Stewardship: each Carbon point you cut gives +2 extra Environment, and Conservation gives +7 Environment instead of +5.',
    ],
    weaknesses: [
      'Coalition deals: everyone present must agree to declare war, leave a treaty or Expand Fossil.',
      'Aging workforce: Prosperity -1 per turn from Turn 10 unless you build Family and Skills (3 Treasury).',
      'Humane refugee response at Turn 14: accept 2 or more refugee groups, or fund resettlement (2 Treasury per group). Doing neither costs 6 Public Support.',
    ],
    ability: {
      id: 'mediator',
      name: 'Mediator of Nations',
      text: 'Once per game. Call a peace conference for two nations. If they are at war, they pause attacks for 1 turn. If they sign any treaty with each other this turn or next, both gain +5 Security and Alvera gains +8 Security.',
      repeatTurns: null,
    },
    climateLever: 'Stewardship: extra Environment for every Carbon cut and stronger Conservation.',
    factions: [
      { id: 'al_church', name: 'Civil Society Associations', wants: 'Aid given and a humane refugee response.' },
      { id: 'al_firms', name: 'Family Firms and Farmers', wants: 'Prosperity of 50 or more and Food security.' },
      { id: 'al_regions', name: 'Regional Governments', wants: 'Resilience and local programs.' },
      { id: 'al_workers', name: 'Workers’ Wing', wants: 'Well-being of 55 or more.' },
    ],
    objectives: ['alvera_solidarity', 'alvera_stewardship'],
    vector: { economy: 0.26, liberty_order: 0.4, tradition_progress: -0.66, sovereignty_global: 0.79, growth_green: 0.53 },
    accent: '#7FB2F0',
    pattern: 'cross',
    roleWords: 'a federation that decides locally and stands by its neighbors',
    ai: {
      securityTarget: 50, greenBias: 1, aidBias: 2, treatyBias: 2, allianceOk: true, usesTariffs: false,
      pledgeRoute: 'solidarity_aid', formula: 'damage', bindingVotes: 'yes',
      programs: ['family_skills', 'health_service', 'schools', 'civic_inclusion', 'farm_support', 'infrastructure', 'public_safety'],
      refugeesAccept: 2,
    },
  },

  karsova: {
    id: 'karsova',
    name: 'The Heritage Republic of Karsova',
    shortName: 'Karsova',
    inspiredBy:
      'European national-conservative and national-populist parties, cited only as European Parliament groups (Patriots for Europe and ECR). It has no specific national, ethnic or religious identity.',
    oneLiner: 'A democracy that runs its own affairs. It puts its heritage, safe borders, self-reliance and the voice of ordinary people first.',
    coreValues:
      'Our shared heritage matters. Our borders are safe. We rely on ourselves. We protect our own farms and factories. A strong elected leader acts fast. The people have more say than faraway groups.',
    government:
      'A presidential democracy with a strong elected leader and frequent referendums. The President may approve a small set of orders alone (decree power). Public Support is visible and starts at 66.',
    rules: {
      signOff: 'head',
      majorSignOff: 'head',
      majorOrders: [],
      fossilIsMajor: false,
      treatyRatify: 'head',
      decree: 1,
      supportVisibility: 'full',
      headRotates: false,
      headTitle: 'President',
      text: 'The President signs orders and treaties. Decree power: the President may act alone on 1 order per turn.',
    },
    start: {
      treasury: 0, energy: 0, food: 3, support: 66,
      forces: { army: 1 },
      index: { prosperity: 2, security: 8, wellbeing: -4, environment: -6 },
      lockedFund: 0,
    },
    flows: { treasury: 0, upkeep: 0, energy: 0, energyUpkeep: 0, food: 1 },
    strengths: [
      'High Public Support (66) and Security (+8).',
      'Self-reliance: +1 Food per turn, and embargoes against you cut your income by only half.',
      'Large population: Army units cost 1 less.',
      'Homegrown Resilience: Resilience and Early Warning cost 1 less, drought Food hits are 1 smaller, and paying for Resilience in a neighbor counts toward your fair share.',
    ],
    weaknesses: [
      'Heritage Tariffs: you start with a standing tariff on every partner (you collect tariff revenue, but each deal yields 1 less unit to both sides). Lifting it for a partner costs 3 Public Support.',
      'Labor shortage: from Turn 10, Prosperity -1 per turn unless you build Workforce Training (3 Treasury) or accept 2 or more refugee groups at Turn 14.',
      'Go-it-alone: you get only half of any Loss and Damage payout unless you pledged at that meeting (which costs 2 Public Support with the Heritage League).',
    ],
    ability: {
      id: 'strategic_autonomy',
      name: 'Strategic Autonomy',
      text: 'Once per game. For 3 turns, ignore all embargoes and sanctions against you and gain +2 Treasury per turn. You may also leave one treaty with no penalty.',
      repeatTurns: null,
    },
    climateLever: 'Homegrown Resilience: getting ready for climate change costs you less. Aid to help a neighbor adapt counts toward your fair share.',
    factions: [
      { id: 'kv_heritage', name: 'Heritage League', wants: 'Tariffs kept and borders secure.' },
      { id: 'kv_farmers', name: 'Home Industry and Farmers', wants: 'Food and Prosperity of 50 or more.' },
      { id: 'kv_border', name: 'Border Security Voters', wants: 'Security of 65 or more.' },
      { id: 'kv_clubs', name: 'Neighborhood and Community Clubs', wants: 'Well-being of 50 or more and local programs.' },
    ],
    objectives: ['karsova_homeland', 'karsova_reliant'],
    vector: { economy: -0.29, liberty_order: 0.48, tradition_progress: -0.67, sovereignty_global: -0.86, growth_green: -0.29 },
    accent: '#D55E00',
    pattern: 'diamond',
    roleWords: 'a self-reliant nation that protects its heritage and borders',
    ai: {
      securityTarget: 65, greenBias: 0, aidBias: 1, treatyBias: 1, allianceOk: true, usesTariffs: true,
      pledgeRoute: 'neighbor_adaptation_aid', formula: 'equal', bindingVotes: 'no',
      programs: ['workforce_training', 'public_safety', 'farm_support', 'schools', 'health_service', 'infrastructure', 'energy_grid'],
      refugeesAccept: 0,
    },
  },

  aurem: {
    id: 'aurem',
    name: 'The Crownlands of Aurem',
    shortName: 'Aurem',
    inspiredBy:
      'Resource-funded monarchies and petro-states pursuing long-term diversification plans, with Norway’s sovereign wealth fund cited as a democratic comparison. No real monarch, dynasty, program, religion or slogan is used.',
    oneLiner: 'A stable kingdom. It uses its oil wealth and a long-range plan to get ready for the world after oil.',
    coreValues:
      'A crown passed down in one family keeps the country steady. We respect tradition. We follow a long-range plan. We save oil money in a national fund. We build new industries before the oil age ends.',
    government:
      'An advisory monarchy. The Crown (Head of State) rules with an appointed Consultative Council; there are no elections. The Crown signs orders and treaties. The Crown role rotates every 5 turns. Public Support is visible and starts at 60.',
    rules: {
      signOff: 'head',
      majorSignOff: 'head',
      majorOrders: [],
      fossilIsMajor: false,
      treatyRatify: 'head',
      decree: 1,
      supportVisibility: 'full',
      headRotates: true,
      headTitle: 'The Crown',
      text: 'The Crown signs orders and treaties and may approve 1 program per turn alone. The Crown role rotates every 5 turns.',
    },
    start: {
      treasury: 4, energy: 6, food: -2, support: 60,
      forces: {},
      index: { prosperity: 6, security: 4, wellbeing: -2, environment: -8 },
      lockedFund: 12,
    },
    flows: { treasury: 0, upkeep: 0, energy: 0, energyUpkeep: 0, food: 0 },
    strengths: [
      'Sovereign Wealth Fund: 12 locked Treasury that earns +1 per turn and can be unlocked once, in whole or in part.',
      'Major energy exporter: +6 Energy at the start.',
      `Stability: Public Support cannot fall below 40 while Royal Grants (${ROYAL_GRANTS} Treasury per turn) are paid.`,
      'Fast decisions: the Crown may approve 1 program per turn without the council.',
    ],
    weaknesses: [
      'Oil-demand decline: from Turn 12 (or the clean-tech tipping point), the World Market pays only 1 Treasury for 3 Energy unless you have 2 or more Diversification levels.',
      'Limited political voice: if Royal Grants are missed, Public Support -10 and an unrest roll (d10 of 1-3: -5 Prosperity).',
      'Food-import dependence: -2 Food at the start.',
    ],
    ability: {
      id: 'vision_decree',
      name: 'Vision Decree',
      text: 'Once per game. Launch a megaproject for 8 Treasury. After 2 turns it gives +10 Prosperity and one free Green Transition (solar). A d10 roll of 1-3 means it is scaled back to half.',
      repeatTurns: null,
    },
    climateLever: 'Sovereign Fund Grant: pledge to the Loss and Damage Fund from the locked fund; it counts toward your fair share and gives +2 Security.',
    factions: [
      { id: 'au_council', name: 'Royal Council', wants: 'Stability and Security of 60 or more.' },
      { id: 'au_merchants', name: 'Merchant Houses', wants: 'Trade deals and Diversification.' },
      { id: 'au_young', name: 'Young Professionals and Entrepreneurs', wants: 'Rising Well-being and Diversification.' },
      { id: 'au_elders', name: 'Traditional Elders', wants: 'Royal Grants paid and gradual change.' },
    ],
    objectives: ['aurem_after_oil', 'aurem_anchor'],
    vector: { economy: 0.28, liberty_order: 0.84, tradition_progress: -0.37, sovereignty_global: 0.28, growth_green: -0.75 },
    accent: '#B8A6E8',
    pattern: 'triangles',
    roleWords: 'a stable monarchy with a long-term plan for life after oil',
    ai: {
      securityTarget: 55, greenBias: 1, aidBias: 1, treatyBias: 2, allianceOk: true, usesTariffs: false,
      pledgeRoute: 'sovereign_fund_grant', formula: 'equal', bindingVotes: 'yes',
      programs: ['diversification', 'health_service', 'schools', 'farm_support', 'infrastructure', 'public_safety', 'civic_inclusion'],
      refugeesAccept: 1,
    },
  },
};

/* ---------------------------------------------------------------------------------------------- */
/* Nation profiles for the Our Nation panel and the Turn 0 country briefing                         */
/* ---------------------------------------------------------------------------------------------- */

/**
 * Student-facing ideology family labels and plain summaries. Every summary has the same three-part shape
 * (core value, how it helps people, what it worries about) so no nation reads as uncaring beside another.
 * `labelOptions` are the teacher's alternatives (the first one is the default). None of this is teacher-only;
 * the real-world `inspiredBy` label above stays teacher-only until the reveal or the debrief.
 */
export interface IdeologyProfile {
  familyLabel: string;
  labelOptions: string[];
  beliefSummary: string;
  /** Four "We believe" bullets drawn from coreValues. */
  beliefs: [string, string, string, string];
  /** The nation's World Agenda id (each nation authors one agenda; its id is the author's id). */
  agenda: CountryId;
}

export const PROFILES: Record<CountryId, IdeologyProfile> = {
  ironvale: {
    familyLabel: 'Free-Enterprise Conservatism',
    labelOptions: ['Free-Enterprise Conservatism', 'Constitutional Conservatism'],
    beliefSummary:
      'Ironvale believes in liberty and limited government under a written constitution. It trusts free enterprise, hard work, low taxes and cheap energy to create jobs. It believes a growing economy lifts families out of hardship. It values strong families, community groups, local towns, and a strong military that keeps the peace. It worries that world rules can cost jobs and freedom. To deal with a changing climate, it would rather invent new tools and get ready than follow world orders.',
    beliefs: [
      'Liberty and limited government under a written constitution.',
      'Free enterprise, low taxes and cheap energy create good jobs.',
      'Strong families, community groups and local towns.',
      'Peace through strength, with allies who carry their share.',
    ],
    agenda: 'ironvale',
  },
  brightwater: {
    familyLabel: 'Modern Liberalism',
    labelOptions: ['Modern Liberalism', 'Social Liberalism', 'Progressive Liberalism'],
    beliefSummary:
      'Brightwater believes government should protect equal rights and give everyone a fair chance. It wants a market economy with rules that protect workers, buyers and nature. It wants a strong safety net and a fast switch to clean energy. It believes nations are safer and richer when they work together through alliances and world groups. It worries that without fair rules, working families and the natural world will carry the costs of change.',
    beliefs: [
      'Equal rights and a fair chance for everyone.',
      'Growth from the middle out, with fair rules for workers and unions.',
      'A strong safety net and a fast switch to clean energy.',
      'Nations are safer when they work together through alliances and world groups.',
    ],
    agenda: 'brightwater',
  },
  meridia: {
    familyLabel: 'Libertarianism',
    labelOptions: ['Libertarianism'],
    beliefSummary:
      'Meridia believes each person should be as free as possible to live, work and trade, as long as they respect the rights of others. It wants a small government, low taxes, sound money, free trade with every nation and peace with all. It trusts charities, clubs and businesses that work together by choice to help people in need. It worries that big governments and world bodies grow too strong, so it stays out of military alliances.',
    beliefs: [
      'Each person should be as free as possible to live, work and trade.',
      'A small government, low taxes and sound money.',
      'Charities, clubs and businesses help people in need by choice.',
      'Free trade and peace with every nation, and no military alliances.',
    ],
    agenda: 'meridia',
  },
  kestran: {
    familyLabel: 'One-Party Market Socialism',
    labelOptions: ['One-Party Market Socialism'],
    beliefSummary:
      'Kestran is led by one party that plans the country’s future many years ahead. Other parties do not run for power, and there are no open national elections. The state guides the biggest industries, while private firms and markets also grow. Its main goal is common prosperity. That means lifting everyone out of poverty and keeping life calm and stable. It worries about pressure from outside, so it believes every nation should run its own affairs.',
    beliefs: [
      'Long-range national plans, led by one party.',
      'Common prosperity: lift everyone out of poverty.',
      'A calm and stable society.',
      'Every nation should run its own affairs without outside pressure.',
    ],
    agenda: 'kestran',
  },
  vellmark: {
    familyLabel: 'Social Democracy',
    labelOptions: ['Social Democracy'],
    beliefSummary:
      'Vellmark believes a fair country makes sure everyone has what they need to do well. People pay high taxes, and in return everyone gets good schools, health care and help in hard times. Most businesses are private and trade freely. Unions and employers bargain together over pay. Vellmark trusts cooperation, at home and with other nations. It worries most about harm to the land and sea, and about people being left behind.',
    beliefs: [
      'Everyone should have what they need to do well.',
      'High taxes pay for good schools, health care and help in hard times.',
      'Unions and employers bargain together over pay.',
      'Work together at home and abroad, and care for the land and sea.',
    ],
    agenda: 'vellmark',
  },
  alvera: {
    familyLabel: 'Christian Democracy',
    labelOptions: ['Christian Democracy', 'Social Market Democracy'],
    beliefSummary:
      'Alvera believes every person has equal dignity. It wants choices made at the most local level that works. It wants strong families and towns, and a market economy that is fair to workers. It believes nations owe each other help in hard times, and it cares for the natural world. It worries when either a big government or the market forgets the person, so it looks for a middle way.',
    beliefs: [
      'Every person has equal dignity.',
      'Make choices at the most local level that works.',
      'Strong families, and a market economy that is fair to workers.',
      'Nations owe each other help in hard times, and we care for the natural world.',
    ],
    agenda: 'alvera',
  },
  karsova: {
    familyLabel: 'National Conservatism',
    labelOptions: ['National Conservatism'],
    beliefSummary:
      'Karsova believes a nation is a community with its own history and way of life. Its government’s first duty is to its own people. It believes strong, united towns take care of their own, and that a nation that can feed and defend itself is a steadier neighbor. It wants secure borders and strong home farms and factories. It worries that distant world bodies do not understand local needs, so it trusts its elected leader and ordinary voters more.',
    beliefs: [
      'A nation is a community with its own history and way of life.',
      'The government’s first duty is to its own people.',
      'Secure borders and strong home farms and factories.',
      'Trust the elected leader and ordinary voters over distant world bodies.',
    ],
    agenda: 'karsova',
  },
  aurem: {
    familyLabel: 'Modernizing Monarchy',
    labelOptions: ['Modernizing Monarchy'],
    beliefSummary:
      'Aurem is ruled by a royal family, called the Crown, with advice from a council instead of elections. It values stability, tradition and planning for the long term. It grew rich by selling oil and gas. It saved part of that money in a national fund, and it uses its wealth to support families. It worries about what happens when the world stops buying oil, so it is building new industries now.',
    beliefs: [
      'Stability and tradition under the Crown.',
      'Plan for the long term.',
      'Save oil wealth in a national fund that supports families.',
      'Build new industries before the oil age ends.',
    ],
    agenda: 'aurem',
  },
};

/** The family label a class sees (the teacher may choose one of the labelOptions). */
export function familyLabelOf(id: CountryId, choices?: Partial<Record<CountryId, string>>): string {
  const p = PROFILES[id];
  const chosen = choices?.[id];
  return chosen && p.labelOptions.includes(chosen) ? chosen : p.familyLabel;
}
